import React, { useState, useEffect, useRef } from 'react';
import { BookOpenIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { getCleanText } from '../utils/htmlRenderer';

interface ArticleContentProps {
  content: string;
  estimatedDuration?: number; // in minutes
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
}

const ArticleContent: React.FC<ArticleContentProps> = ({
  content,
  estimatedDuration = 5,
  onProgress,
  onComplete
}) => {
  const [progress, setProgress] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Track reading progress based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      
      const element = contentRef.current;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;
      
      if (scrollHeight > clientHeight) {
        const scrollProgress = (scrollTop / (scrollHeight - clientHeight)) * 100;
        const newProgress = Math.min(Math.max(scrollProgress, 0), 100);
        
        if (newProgress !== progress) {
          setProgress(newProgress);
          onProgress?.(newProgress);
          
          // Mark as complete when scrolled to bottom
          if (newProgress >= 95) {
            setIsCompleted(true);
            onComplete?.();
          }
        }
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [progress, onProgress, onComplete]);

  // Track time spent reading
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isCompleted) {
        setTimeSpent(prev => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isCompleted]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMarkComplete = () => {
    setProgress(100);
    setIsCompleted(true);
    onProgress?.(100);
    onComplete?.();
  };

  return (
    <div className="space-y-4">
      {/* Article Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <BookOpenIcon className="h-6 w-6 text-blue-600" />
            <div>
              <h3 className="font-semibold text-blue-900 text-sm sm:text-base">Reading Material</h3>
              <p className="text-blue-700 text-xs sm:text-sm">
                Estimated reading time: {estimatedDuration} minutes
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-blue-600">Time spent</div>
            <div className="text-sm font-medium text-blue-900">{formatTime(timeSpent)}</div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div 
        ref={contentRef}
        className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 max-h-96 overflow-y-auto"
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#CBD5E0 #F7FAFC' }}
      >
        <div className="prose prose-sm sm:prose-base max-w-none">
          <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
            {getCleanText(content)}
          </div>
        </div>
      </div>

      {/* Progress Tracking */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Reading Progress</h4>
          <div className="flex items-center space-x-2">
            <ClockIcon className="h-4 w-4 text-gray-500" />
            <span className="text-xs text-gray-600">{formatTime(timeSpent)}</span>
          </div>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-300" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600">{progress.toFixed(1)}% completed</span>
          
          {!isCompleted && (
            <button
              onClick={handleMarkComplete}
              className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              Mark as Complete
            </button>
          )}
          
          {isCompleted && (
            <div className="flex items-center text-green-600">
              <CheckCircleIcon className="h-5 w-5 mr-1" />
              <span className="text-sm font-medium">Completed!</span>
            </div>
          )}
        </div>
      </div>

      {/* Reading Tips */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-900 text-sm sm:text-base mb-2">💡 Reading Tips</h4>
        <ul className="text-yellow-800 text-xs sm:text-sm space-y-1">
          <li>• Take your time to understand the content</li>
          <li>• Scroll through the entire article to mark as complete</li>
          <li>• You can manually mark as complete if needed</li>
          <li>• Progress is tracked based on scroll position</li>
        </ul>
      </div>
    </div>
  );
};

export default ArticleContent;
